import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chart, registerables } from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { statsColor } from '../../assets/colours';
import { ChartModal } from '../../shared/modal/ChartModal';
import { formatAssessmentDate } from '../../shared/util/helperfunctions';
import {
  averageIsDisplayable,
  isMissingChartValue,
  normalizeSeriesValues,
  seriesHasDisplayablePoint,
} from '../utils/chartDataAvailability';
import ChartEmptyState from './ChartEmptyState';

const monthsArr = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const convertToTime = (value) => {
  let timeStr = value.toString();
  if (timeStr.length === 5) {
    timeStr = timeStr.replace('.', ':');
  } else if (timeStr.includes('.')) {
    // let h = timeStr.split(".")[0]
    let m = timeStr.split('.')[1];
    if (m.length === 0) {
      m = '00';
    } else if (m.length === 1) {
      m += '0';
    }
    timeStr = `${timeStr.split('.')[0]}:${m}`;
  } else {
    timeStr += ':00';
  }
  return timeStr;
};

const calculateStressLevel = (level, stress_levels) => {
  if (level <= 1) {
    return stress_levels[0];
  }
  if (level <= 2) {
    return stress_levels[1];
  }
  if (level <= 3) {
    return stress_levels[2];
  }
  if (level <= 4) {
    return stress_levels[3];
  }
  return stress_levels[4];
};

const getStat = (value, units, isTime, hoursWorked, yMaxLimit, outOfYMax) => {
  let stat;
  if (isMissingChartValue(value)) {
    stat = 'No data';
  } else if (yMaxLimit && outOfYMax) {
    stat = `${Number(value).toString()}/${yMaxLimit}`;
  } else if (hoursWorked) {
    stat = `${Number(value).toString()} hours`;
  } else if (isTime) {
    if (Array.isArray(value)) {
      stat = convertToTime(value[1]);
    } else {
      stat = convertToTime(value);
    }
  } else {
    stat = `${Number(value).toString()} ${units}`;
  }
  return stat;
};

const createCustomTooltip = (
  units,
  average_type,
  data_length,
  assessDates,
  yMaxLimit,
  outOfYMax,
  stressDescriptor,
  hoursWorked,
  isTime,
  stress_levels
) => ({
  id: 'customTooltip',

  afterDraw(chart, args, options) {
    if (chart.tooltip?._active[0]) {
      const {
        ctx,
        chartArea: { top, bottom },
      } = chart;
      ctx.save();
      const activePoint = chart.tooltip._active[0];
      const datapoint = activePoint.index;
      const { datasetIndex } = activePoint;

      // get text for tooltip
      const value = chart.getDatasetMeta(datasetIndex).data[datapoint].$context.raw;
      const stat = getStat(value, units, isTime, hoursWorked, yMaxLimit, outOfYMax);

      const date = chart.getDatasetMeta(datasetIndex).iScale._labelItems[datapoint].label;
      let textWidth = ctx.measureText(stat).width + 35; // 35 for padding
      if (stressDescriptor && value !== null) {
        textWidth = ctx.measureText(calculateStressLevel(value, stress_levels)).width + 35;
      }
      if (average_type) {
        const averageWidth = ctx.measureText(average_type).width + 35;
        if (averageWidth > textWidth) {
          textWidth = averageWidth;
        }
      }

      const xPoint = chart.getDatasetMeta(datasetIndex).data[datapoint].tooltipPosition().x;
      const yPoint = chart.getDatasetMeta(datasetIndex).data[datapoint].tooltipPosition().y;

      const labelsLength = chart.getDatasetMeta(datasetIndex).iScale._labelItems.length;

      const activeLabel = chart.getDatasetMeta(datasetIndex).data[datapoint].$context.index;
      // for the last two values show the lable on the left of the bar
      if (activeLabel >= labelsLength - 2) {
        // if it is tracker data
        if (data_length !== 7 && !assessDates) {
          // draw rounded rectangle
          ctx.beginPath();
          if (stressDescriptor && value !== null) {
            ctx.roundRect(xPoint - 105, yPoint - 25, textWidth, 70, 12);
          } else {
            ctx.roundRect(xPoint - 105, yPoint - 25, textWidth, 65, 12);
          }
          ctx.strokeStyle = '#dccfdb';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.fillStyle = '#dccfdb';
          ctx.fill();
          ctx.closePath();

          // draw date
          ctx.font = '10px futura-pt-book';
          ctx.fillStyle = statsColor;
          ctx.textAlign = 'start';
          ctx.fillText(`${date[0]} ${date[1]}`, xPoint - 90, yPoint - 5);

          // draw average
          ctx.font = '15px futura-pt-book';
          ctx.fillText(`${average_type}`, xPoint - 90, yPoint + 10);
          // draw value
          ctx.font = '15px futura-pt-demi';
          ctx.fillText(`${stat}`, xPoint - 90, yPoint + 25);
          // draw stress_level
          if (stressDescriptor && value !== null) {
            ctx.font = '15px futura-pt-book';
            ctx.fillText(`${calculateStressLevel(value, stress_levels)}`, xPoint - 90, yPoint + 35);
          }
        } else {
          // if it is assessment data
          // draw rounded rectangle
          ctx.beginPath();
          if (stressDescriptor && value !== null) {
            ctx.roundRect(xPoint - 105, yPoint - 25, textWidth, 65, 12);
          } else {
            ctx.roundRect(xPoint - 105, yPoint - 25, textWidth, 50, 12);
          }
          ctx.strokeStyle = '#dccfdb';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.fillStyle = '#dccfdb';
          ctx.fill();
          ctx.closePath();

          // draw date
          ctx.font = '10px futura-pt-book';
          ctx.fillStyle = statsColor;
          ctx.textAlign = 'start';
          ctx.fillText(`${date[0]} ${date[1]}`, xPoint - 90, yPoint - 5);

          // draw value
          ctx.font = '15px futura-pt-demi';
          ctx.fillText(`${stat}`, xPoint - 90, yPoint + 10);

          // draw stress_level
          if (stressDescriptor && value !== null) {
            ctx.font = '15px futura-pt-book';
            ctx.fillText(`${calculateStressLevel(value, stress_levels)}`, xPoint - 90, yPoint + 25);
          }
        }
      } else if (data_length !== 7 && !assessDates) {
        // draw rounded rectangle
        ctx.beginPath();
        if (stressDescriptor && value !== null) {
          ctx.roundRect(xPoint + 15, yPoint - 25, textWidth, 80, 12);
        } else {
          ctx.roundRect(xPoint + 15, yPoint - 25, textWidth, 65, 12);
        }
        ctx.strokeStyle = '#dccfdb';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = '#dccfdb';
        ctx.fill();
        ctx.closePath();

        // draw date
        ctx.font = '10px futura-pt-book';
        ctx.fillStyle = statsColor;
        ctx.textAlign = 'start';
        ctx.fillText(`${date[0]} ${date[1]}`, xPoint + 25, yPoint - 5);

        // draw average
        ctx.font = '15px futura-pt-book';
        ctx.fillText(`${average_type}`, xPoint + 25, yPoint + 10);

        // draw value
        ctx.font = '15px futura-pt-demi';
        ctx.fillText(`${stat}`, xPoint + 25, yPoint + 25);

        // draw stress_level
        if (stressDescriptor && value !== null) {
          ctx.font = '15px futura-pt-book';
          ctx.fillText(`${calculateStressLevel(value, stress_levels)}`, xPoint + 25, yPoint + 40);
        }
      } else {
        // draw rounded rectangle
        ctx.beginPath();
        if (stressDescriptor && value !== null) {
          ctx.roundRect(xPoint + 15, yPoint - 25, textWidth, 60, 12);
        } else {
          ctx.roundRect(xPoint + 15, yPoint - 25, textWidth, 50, 12);
        }
        ctx.strokeStyle = '#dccfdb';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = '#dccfdb';
        ctx.fill();
        ctx.closePath();

        // draw date
        ctx.font = '10px futura-pt-book';
        ctx.fillStyle = statsColor;
        ctx.textAlign = 'start';
        ctx.fillText(`${date[0]} ${date[1]}`, xPoint + 25, yPoint - 5);

        // draw value
        ctx.font = '15px futura-pt-demi';
        ctx.fillText(`${stat}`, xPoint + 25, yPoint + 10);

        // draw stress_level
        if (stressDescriptor && value !== null) {
          ctx.font = '15px futura-pt-book';
          ctx.fillText(`${calculateStressLevel(value, stress_levels)}`, xPoint + 25, yPoint + 25);
        }
      }

      ctx.restore();
    }
  },
});

const customMouseOut = {
  id: 'myEventCatcher',
  afterEvent(chart, args, pluginOptions) {
    const { event } = args;
    if (event.type === 'mouseout') {
      if (chart) {
        const dataset = chart.data.datasets[0];
        const dataLength = dataset.data.length;
        const newBackgroundColor = Array(dataLength).fill('#e92364');
        dataset.backgroundColor = newBackgroundColor;
        dataset.data.forEach((el, index) => {
          if (el === 0) {
            newBackgroundColor[index] = 'transparent';
          }
        });

        // reset tick colors
        const labelColors = chart.config.options.scales.x.ticks.color;
        const foundIndex = labelColors.findIndex((x) => x === 'rgba(221,206,219, 1)');
        labelColors[foundIndex] = 'rgba(221,206,219, 0.5)';
        chart.config.options.scales.x.ticks.color = labelColors;

        chart.update();
      }
    }
  },
};

const customBarContinuation = {
  id: 'customBarContinuation',
  beforeDraw: (chart) => {
    const { ctx } = chart;
    const xAxis = chart.scales.x;
    const yAxis = chart.scales.y;
    const { datasets } = chart.data;

    ctx.save();
    ctx.fillStyle = '#362b3c';

    datasets.forEach((dataset) => {
      const { data } = dataset;
      const barSpacing = xAxis.getPixelForValue(1) - xAxis.getPixelForValue(0);
      //   const barWidth = barSpacing * 0.68; // Adjust the width as needed
      const barWidth = 14;

      for (let i = 0; i < data.length; i++) {
        const radius = barWidth / 2;
        const startX = xAxis.left + i * barSpacing + (barSpacing - barWidth) / 2;
        const startY = yAxis.top + radius;

        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(startX, yAxis.bottom - radius);
        ctx.arc(startX + radius, yAxis.bottom - radius, radius, Math.PI, 0, true);
        ctx.lineTo(startX + barWidth, startY);
        ctx.arc(startX + radius, startY, radius, 0, Math.PI, true);
        ctx.closePath();
        ctx.fill();
      }
    });
    ctx.restore();
  },
};

function BarChart(props) {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const showChartShellOnEmpty = Boolean(props.showChartShellOnEmpty);
  // const customTooltipWithUnits = createCustomTooltip(
  //   props.units,
  //   props.average_type,
  //   props.data.length,
  //   props.assessDates,
  //   props.yMaxLimit,
  //   props.outOfYMax,
  //   props.stressDescriptor,
  //   props.hoursWorked,
  //   props.isTime
  // );
  const [dataAvailable, setDataAvailable] = useState(true);
  const [showChartDescription, setShowChartDescription] = useState(false);
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    Chart.register(...registerables);
    const labels = [];
    let dataArr = [];
    const labelColor = [];
    let highest = 0;
    let lowest = 0;
    let assessmentDate;
    let dateIndex;
    if (props.assessDates) {
      assessmentDate = formatAssessmentDate(props.assessDates[props.assessmentNumber]).slice(0, -5);
    } else {
      const currentDate = new Date().toJSON().slice(0, 10);
      assessmentDate = formatAssessmentDate(currentDate).slice(0, 6);
    }
    const normalizedData = normalizeSeriesValues(props.data);
    const hasDisplayableData = seriesHasDisplayablePoint(normalizedData);

    if (normalizedData.length > 0) {
      normalizedData.forEach((item, index) => {
        let { date } = item;
        if (!props.assessDates && props.data.length !== 12) {
          date = formatAssessmentDate(date.slice(0, 10)).slice(0, 6);
        } else if (!props.assessDates && props.data.length === 12) {
          const newDate = new Date(date);
          date = newDate.getFullYear().toString();
        }
        if (date.startsWith('0')) date = date.substring(1);
        if (assessmentDate === date) {
          labelColor.push('#fff');
          dateIndex = index;
        } else {
          labelColor.push('rgba(221,206,219, 0.5)');
        }
        date = date.toUpperCase();
        if (!props.assessDates && props.data.length === 5) {
          labels.push([date.split(' ')[1], `WEEK ${index + 1}`]);
        } else if (!props.assessDates && props.data.length === 12) {
          labels.push([monthsArr[index], date.substring(2)]);
        } else {
          labels.push([date.split(' ')[0], date.split(' ')[1]]);
        }
        dataArr.push(item.value);
      });
      const numericValues = dataArr.filter((item) => item !== null && item !== undefined);
      highest = numericValues.length > 0 ? Math.max(...numericValues) : 0;
      if (props.isTime) {
        highest = Math.ceil(highest) + 2;
        if (highest > 24) highest = 24;
        if (numericValues.length > 0) {
          lowest = Math.floor(Math.min(...numericValues)) - 2;
          if (lowest < 0) lowest = 0;
        }
        if (lowest > 0 && numericValues.length > 0) {
          dataArr = dataArr.map((item, index) => {
            if (item !== null) return (item = [lowest, item]);
          });
        }
      } else if (props.yMaxLimit && !props.isTime) {
        highest = props.yMaxLimit;
      } else if (highest <= 20) {
        highest = Math.ceil(highest / 5) * 5;
      } else {
        highest = Math.ceil(highest / 10) * 10;
      }

      // Keep a minimal y-range so hover hit areas remain available
      // for empty/zero-only series (e.g. "No data" nutrition bars).
      if (highest === 0) {
        highest = 1;
      }

      // if there are less than 5 assessments, add empty data
      if (dataArr.length < 5 && dataArr.length > 0) {
        for (let x = dataArr.length; x < 5; x++) {
          labelColor.push('rgba(221,206,219, 0.5)');
          dataArr.push(null);
          labels.push(['DD', 'MM']);
        }
      }

      setDataAvailable(hasDisplayableData);
    } else {
      setDataAvailable(false);
    }

    const tableData = normalizedData.map((item, index) => ({
      date: labels[index] ? `${labels[index][0]} ${labels[index][1]}` : item.date,
      value: getStat(
        item.value,
        props.units,
        props.isTime,
        props.hoursWorked,
        props.yMaxLimit,
        props.outOfYMax
      ),
    }));
    setTableData(tableData);

    const data = {
      labels,
      datasets: [
        {
          data: dataArr,
          backgroundColor: (context) => {
            return '#e92364';
          },
          hoverBackgroundColor: (context) => {
            return '#e92364';
          },
          barThickness: 14, // number (pixels) or 'flex'
          borderRadius: Number.MAX_VALUE,
          borderSkipped: false,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 50,
          right: 20,
          bottom: 0,
          left: 0,
        },
      },
      indexAxis: 'x',
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            beginAtZero: true,
            font: 'futura-pt-medium',
            size: 12.5,
            color: labelColor,
          },
        },
        y: {
          max: highest,
          min: lowest,
          ticks: {
            display: hasDisplayableData,
            precision: 0,
          },
          beginAtZero: true,
          grid: { display: false },
          border: {
            display: false,
          },
          position: 'right',
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
      onHover: (event, activeElements) => {
        const { chart } = event;

        if (chart && hasDisplayableData) {
          const dataset = chart.data.datasets[0];
          const dataLength = dataset.data.length;
          const newBackgroundColor = Array(dataLength).fill('#615668');

          labelColor.forEach((item, index) => {
            if (index === dateIndex) {
              labelColor[index] = '#fff';
            } else {
              labelColor[index] = 'rgba(221,206,219, 0.5)';
            }
          });

          if (activeElements.length > 0) {
            activeElements.forEach((activeElement) => {
              const { datasetIndex, index } = activeElement;
              newBackgroundColor[index] = '#e92364';
              // Change the active tick label opacity to 1
              if (index === dateIndex) {
                labelColor[index] = '#fff';
              } else {
                labelColor[index] = 'rgba(221,206,219, 1)';
              }
            });
          }
          chart.config.options.scales.x.ticks.color = labelColor;
          dataset.backgroundColor = newBackgroundColor;
          chart.update();
        }
      },
    };

    const shouldRenderCanvas = hasDisplayableData || showChartShellOnEmpty;
    if (chartRef && chartRef.current && shouldRenderCanvas) {
      const { chartInstance } = chartRef.current;
      if (chartInstance) {
        chartInstance.destroy();
      }
      chartRef.current.chartInstance = new Chart(chartRef.current.getContext('2d'), {
        type: 'bar',
        data,
        options,
        plugins: [
          customMouseOut,
          customBarContinuation,
          createCustomTooltip(
            props.units,
            props.average_type,
            normalizedData.length,
            props.assessDates,
            props.yMaxLimit,
            props.outOfYMax,
            props.stressDescriptor,
            props.hoursWorked,
            props.isTime,
            props.stress_levels
          ),
        ],
      });
    } else if (chartRef && chartRef.current) {
      const { chartInstance } = chartRef.current;
      if (chartInstance) {
        chartInstance.destroy();
      }
    }

    return () => {
      if (chartRef && chartRef.current && chartRef.current.chartInstance) {
        chartRef.current.chartInstance.destroy();
      }
    };
  }, [props.data, props.assessmentNumber, props.average]);

  const titleId = props.title?.replace(/\s+/g, '');
  if (!dataAvailable && !showChartShellOnEmpty) {
    return (
      <ChartEmptyState
        title={props.title}
        width={props.width}
        openModal={props.openModal}
        modalText={props.modalText}
      />
    );
  }

  return (
    <div
      className={`chart-container ${props.width}`}
      role="group"
      aria-labelledby={`${titleId}-bar-chart-title`}
    >
      <div className="title-container">
        <h3 className="title" id={`${titleId}-bar-chart-title`}>
          {props.title}
        </h3>
        <button
          className="wtf transparent-button"
          onClick={() => props.openModal(props.title, props.modalText)}
          type="button"
          aria-label={t('open.modal.chart.information')}
        >
          <span className="visuallyHidden">{t('open.modal.chart.information')}</span>
          <FontAwesomeIcon icon={faQuestionCircle} className="wtf icon" />
        </button>
      </div>
      <div className="charts-box">
        {!dataAvailable && showChartShellOnEmpty && (
          <div className="chart-no-data">{t('chart.noDataAvailable')}</div>
        )}
        <div className="chart-description-container">
          <div className="chart-wrap fs-mask">
            <canvas
              role="img" // eslint-disable-line
              ref={chartRef}
              aria-label={`${t('chart.bar.description')} ${props.title} ${props.assessmentNumber ? t('results.assessment.data') : t('results.tracker.data')}`}
              aria-hidden={!dataAvailable}
            />
          </div>
          {dataAvailable && (
            <button
              className="chart-description fs-exclude"
              onClick={() => setShowChartDescription(true)}
              type="button"
              aria-label={t('results.modal.button')}
            >
              <span className="sr-only">{props.title}</span>
              {t('results.modal.button')}
              <span className="sr-only">{t('results.modal.button.description')}</span>
            </button>
          )}
        </div>
        {dataAvailable && showChartDescription && (
          <ChartModal
            title={`${props.title.toLowerCase()} ${props.assessmentNumber ? t('results.assessment.data') : t('results.tracker.data')}`}
            onClose={() => setShowChartDescription(false)}
            data={tableData}
          />
        )}
        <div className="average">
          <div className="title">{props.average_type}</div>
          <div className="stats" style={{ color: props.color }}>
            <FontAwesomeIcon icon={props.icon} className="icon alarm" />
            <div className="stat fs-mask">
              {averageIsDisplayable(props.average)
                ? props.isTime
                  ? convertToTime(props.average)
                  : props.units === t('D076')
                    ? Number(props.average).toFixed().toString()
                    : Number(props.average).toString()
                : ' '}
              {/* {props.units && <span>{props.units}</span>} */}
            </div>
            {(props.units || !averageIsDisplayable(props.average)) && (
              <div className={`unit ${averageIsDisplayable(props.average) ? '' : 'no-data-unit'}`}>
                {averageIsDisplayable(props.average) ? props.units : t('chart.noDataAvailable')}
              </div>
            )}
            {props.stressDescriptor &&
              props.stress_levels &&
              dataAvailable &&
              averageIsDisplayable(props.average) && (
                <div className="av-descr fs-mask">
                  {calculateStressLevel(props.average, props.stress_levels)}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BarChart;
